import type { IEmailBuilderMediaItem } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import i18n from "i18next";
import {
  GetEmailBuilderMedia,
  GetEmailBuilderStockMedia,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilder";
import { StatusCode } from "api/protocols";
import { registerEmailBuilderLibraryMediaSource, registerEmailBuilderStockMediaCatalogSlug } from "../domain/media";
import { resolveEmailBuilderFacilityLogoSource } from "./emailBuilderFacilityLogo";

export const EMAIL_BUILDER_FACILITY_LOGO_MEDIA_ID = 0;

export const EMAIL_BUILDER_MEDIA_ALLOWED_FILE_TYPES = ["png", "jpg"] as const;

export const EMAIL_BUILDER_MEDIA_ALLOWED_SUBTYPES = [
  "jpeg",
  "jpg",
  "png",
  "gif",
  "webp",
] as const;

export function isEmailBuilderMediaImageSubtype(subtype: string): boolean {
  const normalized = subtype.toLowerCase();
  return (EMAIL_BUILDER_MEDIA_ALLOWED_SUBTYPES as readonly string[]).includes(normalized);
}

export function filterActiveEmailBuilderMedia(
  media: IEmailBuilderMediaItem[],
): IEmailBuilderMediaItem[] {
  return media.filter(
    item => isEmailBuilderMediaImageSubtype(item.subtype) && item.archived_at == null,
  );
}

export function filterActiveEmailBuilderStockMedia(
  media: IEmailBuilderMediaItem[],
): IEmailBuilderMediaItem[] {
  return media.filter(item => isEmailBuilderMediaImageSubtype(item.subtype));
}

export const EMAIL_BUILDER_SOCIAL_ICON_CATALOG_SLUGS = [
  "facebook",
  "instagram",
  "x",
] as const;

const EMAIL_BUILDER_SOCIAL_ICON_CATALOG_SLUG_ORDER = new Map<string, number>(
  EMAIL_BUILDER_SOCIAL_ICON_CATALOG_SLUGS.map((slug, index) => [slug, index]),
);

export function filterSocialIconStockMedia(
  media: IEmailBuilderMediaItem[],
): IEmailBuilderMediaItem[] {
  return filterActiveEmailBuilderStockMedia(media)
    .filter(
      item =>
        item.catalog_slug !== null &&
        (EMAIL_BUILDER_SOCIAL_ICON_CATALOG_SLUGS as readonly string[]).includes(
          item.catalog_slug,
        ),
    )
    .sort((left, right) => {
      const leftOrder =
        left.catalog_slug == null
          ? Number.MAX_SAFE_INTEGER
          : (EMAIL_BUILDER_SOCIAL_ICON_CATALOG_SLUG_ORDER.get(left.catalog_slug) ??
            Number.MAX_SAFE_INTEGER);
      const rightOrder =
        right.catalog_slug == null
          ? Number.MAX_SAFE_INTEGER
          : (EMAIL_BUILDER_SOCIAL_ICON_CATALOG_SLUG_ORDER.get(right.catalog_slug) ??
            Number.MAX_SAFE_INTEGER);
      return leftOrder - rightOrder;
    });
}

export function upsertEmailBuilderMediaItem(
  media: IEmailBuilderMediaItem[],
  nextItem: IEmailBuilderMediaItem,
): IEmailBuilderMediaItem[] {
  const withoutCurrent = media.filter(item => item.id !== nextItem.id);
  return [nextItem, ...withoutCurrent];
}

export function isEmailBuilderFacilityLogoMediaId(id: number): boolean {
  return id === EMAIL_BUILDER_FACILITY_LOGO_MEDIA_ID;
}

export function createEmailBuilderFacilityLogoMediaItem(
  facility: {
    id?: number;
    logo_source?: string | null;
    long_name?: string | null;
  } | null,
): IEmailBuilderMediaItem | null {
  if (facility == null) {
    return null;
  }

  const source = resolveEmailBuilderFacilityLogoSource(facility.logo_source);
  const longName =
    typeof facility.long_name === "string" && facility.long_name.trim().length > 0
      ? facility.long_name.trim()
      : i18n.t("Facility logo");

  return {
    id: EMAIL_BUILDER_FACILITY_LOGO_MEDIA_ID,
    facility_id: facility.id ?? 0,
    stock: false,
    source: source ?? "",
    filename: longName,
    catalog_slug: null,
    type: "image",
    subtype: "png",
    size: 0,
    archived_at: null,
    created_at: null,
    updated_at: null,
  };
}

export function registerEmailBuilderLibraryMediaItems(
  items: IEmailBuilderMediaItem[],
): void {
  for (const item of items) {
    registerEmailBuilderLibraryMediaSource(item.id, item.source);
    if (item.stock && item.catalog_slug) {
      registerEmailBuilderStockMediaCatalogSlug(item.catalog_slug, item.source);
    }
  }
}

export { emailBuilderMediaItemToMediaRef } from "./emailBuilderMediaRef";

export async function prefetchEmailBuilderLibraryMediaSources(): Promise<void> {
  const [facilityRes, stockRes] = await Promise.all([
    GetEmailBuilderMedia(false),
    GetEmailBuilderStockMedia(false),
  ]);

  if (facilityRes.status === StatusCode.OK && Array.isArray(facilityRes.data)) {
    registerEmailBuilderLibraryMediaItems(facilityRes.data);
  }

  if (stockRes.status === StatusCode.OK && Array.isArray(stockRes.data)) {
    registerEmailBuilderLibraryMediaItems(stockRes.data);
  }
}
